import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook to manage resizable table columns with localStorage persistence.
 *
 * @param {string} storageKey - Unique localStorage key to persist column widths
 * @param {Array<{ id: string, label: string, defaultWidth: number, minWidth?: number, resizable?: boolean }>} columnDefs - Column definitions
 * @returns {{
 *   columnWidths: Record<string, number>,
 *   isResizing: boolean,
 *   resizingCol: string | null,
 *   onStartResize: (columnId: string, event: React.MouseEvent) => void,
 *   resetWidths: () => void,
 *   getColumnWidth: (columnId: string) => number,
 *   getTotalWidth: (visibleColIds: string[]) => number
 * }}
 */
export function useResizableColumns(storageKey, columnDefs = [], onResized) {
  const onResizedRef = useRef(onResized);
  onResizedRef.current = onResized;
  const defaultWidths = useRef({});
  const minWidths = useRef({});

  columnDefs.forEach((col) => {
    defaultWidths.current[col.id] = col.defaultWidth;
    minWidths.current[col.id] = col.minWidth ?? 60;
  });

  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      if (storageKey) {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed && typeof parsed === 'object') {
            const merged = { ...defaultWidths.current };
            Object.keys(parsed).forEach((k) => {
              if (typeof parsed[k] === 'number' && !Number.isNaN(parsed[k])) {
                const min = minWidths.current[k] ?? 60;
                merged[k] = Math.max(min, parsed[k]);
              }
            });
            return merged;
          }
        }
      }
    } catch (e) {
      console.warn('Failed to load column widths from localStorage:', e);
    }
    return { ...defaultWidths.current };
  });

  const [isResizing, setIsResizing] = useState(false);
  const [resizingCol, setResizingCol] = useState(null);

  const widthsRef = useRef(columnWidths);
  useEffect(() => {
    widthsRef.current = columnWidths;
  }, [columnWidths]);

  const saveWidthsToStorage = useCallback(
    (widths) => {
      if (!storageKey) return;
      try {
        localStorage.setItem(storageKey, JSON.stringify(widths));
      } catch (e) {
        console.warn('Failed to save column widths to localStorage:', e);
      }
    },
    [storageKey],
  );

  const resetWidths = useCallback(() => {
    const defaults = { ...defaultWidths.current };
    setColumnWidths(defaults);
    widthsRef.current = defaults;
    if (storageKey) {
      try {
        localStorage.removeItem(storageKey);
      } catch (e) {
        console.warn('Failed to clear column widths from localStorage:', e);
      }
    }
  }, [storageKey]);

  const onStartResize = useCallback(
    (colId, e) => {
      if (e.button != null && e.button !== 0) return;

      e.preventDefault();
      e.stopPropagation();

      const pointerId = e.pointerId;
      const startX = e.clientX;
      const headerCell = e.currentTarget?.closest?.('th');
      const measured = headerCell
        ? Math.round(headerCell.getBoundingClientRect().width)
        : 0;
      const startWidth =
        measured ||
        widthsRef.current[colId] ||
        defaultWidths.current[colId] ||
        100;
      const minWidth = minWidths.current[colId] ?? 60;

      setIsResizing(true);
      setResizingCol(colId);

      const prevCursor = document.body.style.cursor;
      const prevUserSelect = document.body.style.userSelect;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      let currentWidth = startWidth;
      let moved = false;

      const handlePointerMove = (moveEvent) => {
        if (pointerId != null && moveEvent.pointerId !== pointerId) return;
        moveEvent.preventDefault();
        const deltaX = moveEvent.clientX - startX;
        if (deltaX !== 0) moved = true;
        currentWidth = Math.max(minWidth, Math.round(startWidth + deltaX));

        setColumnWidths((prev) => {
          const next = { ...prev, [colId]: currentWidth };
          widthsRef.current = next;
          return next;
        });
      };

      const handlePointerUp = (upEvent) => {
        if (pointerId != null && upEvent.pointerId !== pointerId) return;
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevUserSelect;
        setIsResizing(false);
        setResizingCol(null);

        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
        window.removeEventListener('pointercancel', handlePointerUp);

        if (moved) {
          const killClick = (clickEvent) => {
            clickEvent.preventDefault();
            clickEvent.stopPropagation();
            window.removeEventListener('click', killClick, true);
          };
          window.addEventListener('click', killClick, true);
        }

        if (moved) onResizedRef.current?.(colId);
        saveWidthsToStorage(widthsRef.current);
      };

      window.addEventListener('pointermove', handlePointerMove, {
        passive: false,
      });
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerUp);
    },
    [saveWidthsToStorage],
  );

  const commitWidths = useCallback((map) => {
    setColumnWidths((prev) => {
      const next = { ...prev, ...map };
      widthsRef.current = next;
      return next;
    });
  }, []);

  const getColumnWidth = useCallback((colId) => {
    return widthsRef.current[colId] || defaultWidths.current[colId] || 100;
  }, []);

  const getTotalWidth = useCallback((visibleColIds = []) => {
    return visibleColIds.reduce((sum, colId) => {
      const w = widthsRef.current[colId] || defaultWidths.current[colId] || 100;
      return sum + w;
    }, 0);
  }, []);

  return {
    columnWidths,
    isResizing,
    resizingCol,
    onStartResize,
    commitWidths,
    resetWidths,
    getColumnWidth,
    getTotalWidth,
  };
}
